import { useEffect } from 'react';
import { useReactFlow, useStoreApi } from '@xyflow/react';

/** How long after a `fullscreenchange` a canvas size change still counts as its resize. */
const RESIZE_WINDOW_MS = 1000;

/**
 * Keeps the flow point at the centre of the view there while the canvas changes size on entering or leaving full
 * screen. React Flow keeps the viewport's top-left corner through a resize, so the view would drift by half the
 * size change; nothing is fitted again. The event fires before the browser reports the new size, so the size the
 * store holds then is the old one; each size change in the window after it moves the view by half its step.
 */
export function FullscreenHold() {
  const store = useStoreApi();
  const { getViewport, setViewport } = useReactFlow();
  useEffect(() => {
    let last: { width: number; height: number } | null = null;
    let timer = 0;
    const unsubscribe = store.subscribe(({ width, height }) => {
      if (last === null || width <= 0 || height <= 0) return;
      if (width === last.width && height === last.height) return;
      const step = { x: (width - last.width) / 2, y: (height - last.height) / 2 };
      // Before the move: setting the viewport updates the store, which calls this listener again at once.
      last = { width, height };
      const { x, y, zoom } = getViewport();
      void setViewport({ x: x + step.x, y: y + step.y, zoom });
    });
    const onChange = () => {
      const { width, height } = store.getState();
      last = { width, height };
      window.clearTimeout(timer);
      timer = window.setTimeout(() => (last = null), RESIZE_WINDOW_MS);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => {
      unsubscribe();
      document.removeEventListener('fullscreenchange', onChange);
      window.clearTimeout(timer);
    };
  }, [store, getViewport, setViewport]);
  return null;
}
